<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <div>
                    <h1 class="text-left text-h5 font-weight-bold">My Devices</h1>
                    <p class="text-body-2 text-medium-emphasis text-left mb-0">Manage and monitor your trackers</p>
                </div>
                <v-btn v-if="canRegister" color="primary" prepend-icon="mdi-plus" @click="assignDialogSwitch = true">
                    Register Device
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="expiredDeviceCount > 0 || expiringDeviceCount > 0">
            <v-col cols="12" class="px-5">
                <v-alert v-if="expiredDeviceCount > 0" type="error" variant="tonal" density="compact" class="mb-2" closable>
                    <strong>{{ expiredDeviceCount }} device(s) have expired subscriptions.</strong>
                    Tracking services are paused. Please renew to continue tracking.
                    <template v-slot:append>
                        <v-btn v-if="authStore.isCircleOwner" size="small" variant="outlined" color="error" @click="navigateTo('invoices')">View Invoices</v-btn>
                        <span v-else class="text-caption">Ask your circle owner to settle this.</span>
                    </template>
                </v-alert>
                <v-alert v-if="expiringDeviceCount > 0" type="warning" variant="tonal" density="compact" closable>
                    <strong>{{ expiringDeviceCount }} device(s) expiring within 30 days.</strong>
                    Renew early to avoid service interruption.
                    <template v-slot:append>
                        <v-btn v-if="authStore.isCircleOwner" size="small" variant="outlined" color="warning" @click="navigateTo('invoices')">View Invoices</v-btn>
                        <span v-else class="text-caption">Ask your circle owner to renew.</span>
                    </template>
                </v-alert>
            </v-col>
        </v-row>

        <v-row v-if="device_counts.length">
            <v-col cols="12" class="pt-0">
                <v-chip-group>
                    <v-chip density="comfortable" variant="tonal" color="primary">
                        <v-icon start size="16">mdi-devices</v-icon>
                        Total: {{ totalDevices }}
                    </v-chip>
                    <v-chip v-for="device_count in device_counts" :key="device_count.status"
                            density="comfortable" variant="tonal"
                            :color="statusChipColor(device_count.status)">
                        {{ status(device_count.status) }}: {{ device_count.count }}
                    </v-chip>
                </v-chip-group>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <div class="d-flex justify-end mb-2 ga-1">
                    <v-btn icon="mdi-filter-variant" size="small" variant="text" title="Filter devices"
                           @click="filtersDialogSwitch = true"/>
                    <v-menu v-if="Object.keys(activeFilters).length">
                        <template v-slot:activator="{ props }">
                            <v-badge color="primary" :content="Object.keys(activeFilters).length" inline>
                                <v-btn icon="mdi-filter-menu" size="small" v-bind="props" variant="text"
                                       title="Active filters"/>
                            </v-badge>
                        </template>

                        <v-list class="border pa-0" density="compact">
                            <v-list-item v-for="(filter,key) in activeFilters" :key="key" class="py-0 border-b">
                                <div class="d-flex justify-space-between align-center">
                                    <span class="text-body-2">{{ filter.label }}</span>
                                    <v-btn icon="mdi-close" size="x-small" variant="plain" @click="removeFilter(key)"/>
                                </div>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>

                <v-card variant="outlined" rounded="lg">
                    <v-table hover>
                        <thead>
                        <tr>
                            <th style="width: 80px">Status</th>
                            <th style="width: 40px"></th>
                            <th-btn :sortedColumn="sortedColumn" column="label" sortable title="Name" @sort="sort"/>
                            <th-btn :sortedColumn="sortedColumn" column="slno" sortable title="Device Sl. No"
                                    @sort="sort"/>
                            <th>Location</th>
                            <th>Last Reported</th>
                            <th>Subscription</th>
                            <th>Type</th>
                            <th>Version</th>
                            <th>SKU</th>
                            <th class="text-center" style="width: 80px">Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        <template v-if="loading">
                            <tr>
                                <td colspan="11" class="text-center pa-8">
                                    <v-progress-circular color="primary" indeterminate size="32"/>
                                    <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Loading devices...</p>
                                </td>
                            </tr>
                        </template>
                        <template v-else-if="devices.length">
                            <tr v-for="device in devices" :key="device.id" class="text-left">
                                <td>
                                    <div class="d-flex align-center ga-1">
                                        <v-tooltip location="top">
                                            <template v-slot:activator="{ props }">
                                                <v-icon size="16" v-bind="props"
                                                        :icon="signals(device.signal_bars??'')?.icon ?? 'mdi-signal-off'"
                                                        :color="signals(device.signal_bars??'')?.color ?? '#dbdbdb'"/>
                                            </template>
                                            <span>Signal</span>
                                        </v-tooltip>
                                        <v-icon size="16" v-bind="device.stats['battery']"/>
                                        <small v-if="device.stats['battery_label']" class="text-medium-emphasis ms-1" style="font-size: 11px">{{ device.stats['battery_label'] }}</small>
                                        <v-tooltip location="top">
                                            <template v-slot:activator="{ props }">
                                                <v-icon size="10" v-bind="props"
                                                        :color="device.stats['status_color']['color']">
                                                    mdi-circle
                                                </v-icon>
                                            </template>
                                            <span>{{ device.stats['status_color']['message'] }}</span>
                                        </v-tooltip>
                                    </div>
                                </td>
                                <td class="pa-0">
                                    <SubjectAvatar :device="device" :size="28" :show-status="false"/>
                                </td>
                                <td class="font-weight-medium">
                                    <div>{{ device.label }}</div>
                                    <!-- Hiding a tracker is a setting the owner will forget they
                                         applied, and this list is the only place it can be found
                                         again: an unbadged row is indistinguishable from a tracker
                                         that has quietly broken. Red, but tonal rather than the
                                         solid dark red this app uses for a tracker that has stopped
                                         reporting - this is a deliberate setting, not a fault. -->
                                    <v-chip v-if="device.hidden_on_map" size="x-small" color="error"
                                            variant="tonal" class="mt-1" prepend-icon="mdi-map-marker-off">
                                        Hidden from map
                                    </v-chip>
                                </td>
                                <td class="text-medium-emphasis">{{ device.slno }}</td>
                                <td>
                                    <div v-if="isValidAddress(device.formatted_address)" class="d-flex align-center">
                                        <span class="text-body-2 text-truncate cursor-pointer"
                                              style="max-width: 200px"
                                              @click="openStreetView(device)"
                                              :title="device.formatted_address">
                                            {{ device.formatted_address }}
                                        </span>
                                        <v-btn density="compact" icon="mdi-content-copy" size="x-small"
                                               variant="text" class="ml-1"
                                               title="Copy address"
                                               @click="copyAddress(device)"/>
                                    </div>
                                    <span v-else class="text-body-2 text-medium-emphasis">-</span>
                                </td>
                                <td class="text-body-2">{{ formatDateTime(device.last_updated) }}</td>
                                <td>
                                    <v-chip v-if="subscriptionStatus(device) === 'expired'" size="small" color="red" variant="tonal" prepend-icon="mdi-alert-circle">
                                        Expired
                                    </v-chip>
                                    <v-chip v-else-if="subscriptionStatus(device) === 'expiring'" size="small" color="amber-darken-2" variant="tonal" prepend-icon="mdi-clock-alert">
                                        Expiring Soon
                                    </v-chip>
                                    <v-chip v-else-if="subscriptionStatus(device) === 'active'" size="small" color="success" variant="tonal">
                                        Active
                                    </v-chip>
                                    <span v-else class="text-grey text-caption">---</span>
                                </td>
                                <td>
                                    <v-chip v-if="personaByAssetTypeId(device.asset_type_id)" size="small"
                                            variant="tonal"
                                            :prepend-icon="personaByAssetTypeId(device.asset_type_id).icon">
                                        {{ personaByAssetTypeId(device.asset_type_id).short }}
                                    </v-chip>
                                    <v-chip v-else-if="assetType(device.asset_type_id)" size="small" variant="tonal">
                                        {{ assetType(device.asset_type_id)?.name }}
                                    </v-chip>
                                </td>
                                <td class="text-medium-emphasis text-body-2">{{ device.app_version ?? '-' }}</td>
                                <td class="text-medium-emphasis text-body-2">{{ device.sku ?? '-' }}</td>
                                <td class="text-center">
                                    <v-btn v-if="canRename" icon="mdi-pencil-outline" size="small" variant="text" color="primary"
                                           title="Edit device" @click="editDevice(device)"/>
                                    <span v-else class="text-medium-emphasis text-caption">View only</span>
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr>
                                <td colspan="11" class="text-center pa-10">
                                    <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-radar</v-icon>
                                    <p class="text-body-1 text-medium-emphasis mb-1">No devices registered</p>
                                    <p class="text-body-2 text-medium-emphasis mb-3">
                                        {{ canRegister ? 'Register your first device to start tracking' : 'No trackers are shared in this circle yet' }}
                                    </p>
                                    <v-btn v-if="canRegister" color="primary" prepend-icon="mdi-plus" size="small"
                                           @click="assignDialogSwitch = true">
                                        Register Device
                                    </v-btn>
                                </td>
                            </tr>
                        </template>
                        </tbody>
                    </v-table>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @update:model-value="fetchDevices"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>

    <Assign-Device v-if="assignDialogSwitch" v-model="assignDialogSwitch" @addDevice="fetchDevices"/>
    <filters-dialog
        v-if="filtersDialogSwitch"
        v-model="filtersDialogSwitch"
        :filters="filters"

        @applyFilters="()=>{currentPage=1;fetchDevices()}"
        @resetFilters="clearFilters"
    />

    <v-dialog v-model="editDialogSwitch" persistent scrollable width="400">
        <v-form class="mb-3" validate-on="input lazy" @submit.prevent="updateDevice">
            <v-card rounded="lg">
                <v-card-title class="d-flex justify-space-between align-center pa-4">
                    <span class="text-h6">Edit Device</span>
                    <v-btn icon="mdi-close" size="small" variant="text" @click="editDialogSwitch = false"/>
                </v-card-title>
                <v-divider/>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12">
                            <v-img v-if="image" :src="image" class="mb-3" width="200"/>
                            <v-file-input v-model="imageFile" accept="image/*"
                                          density="compact" label="Photo (optional)"
                                          @change="imagePreview" @click:clear="image=null"/>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field v-model="selectedDevice['label']"
                                          :rules="[vRules.required, vRules.minLength(3), vRules.maxLength(100)]"
                                          density="compact"
                                          label="Device Name"/>
                        </v-col>
                        <v-col cols="12">
                            <label class="text-body-2 text-medium-emphasis">What are you tracking?</label>
                            <div class="persona-picker mt-2">
                                <div v-for="p in personas" :key="p.id"
                                     class="persona-option"
                                     :class="{'persona-option--selected': selectedDevice['asset_type_id'] === p.assetTypeId}"
                                     :style="selectedDevice['asset_type_id'] === p.assetTypeId ? {background: p.color, borderColor: p.color} : {}"
                                     @click="selectedDevice['asset_type_id'] = p.assetTypeId">
                                    <v-icon :color="selectedDevice['asset_type_id'] === p.assetTypeId ? 'white' : p.color"
                                            size="22">{{ p.icon }}
                                    </v-icon>
                                    <span class="persona-option-label"
                                          :class="{'text-white': selectedDevice['asset_type_id'] === p.assetTypeId}">
                                        {{ p.short }}
                                    </span>
                                </div>
                            </div>
                            <p class="text-caption text-medium-emphasis mt-2 mb-0">
                                Changing this just updates the look and alerts. Your saved zones and history stay put.
                            </p>
                        </v-col>
                        <v-col v-if="selectedIsPerson && memberOptions.length" cols="12">
                            <label class="text-body-2 text-medium-emphasis">Who is this? (optional)</label>
                            <v-select v-model="selectedDevice['member_user_id']" :items="memberOptions"
                                      item-title="title" item-value="value" density="compact" variant="outlined"
                                      class="mt-2" clearable placeholder="Not a member - just a label"
                                      hint="Link this device to a family member so all their devices share their photo."
                                      persistent-hint/>
                        </v-col>
                        <!-- Battery voltage range: hardware vehicle trackers only, so the
                             battery % reads correctly. Named presets fill canonical values. -->
                        <v-col v-if="selectedIsVehicle && !selectedDevice['is_phone']" cols="12">
                            <label class="text-body-2 text-medium-emphasis">Battery voltage range</label>
                            <v-select v-model="selectedDevice['battery_voltage_preset']"
                                      :items="[
                                          {title: '12V (car / light vehicle)', value: '12v'},
                                          {title: '24V (truck / RV)', value: '24v'},
                                          {title: '48V (golf cart / EV)', value: '48v'},
                                          {title: 'Custom', value: 'custom'},
                                      ]"
                                      item-title="title" item-value="value" density="compact" variant="outlined"
                                      class="mt-2" placeholder="12V"
                                      hint="Sets the voltage so the battery % reads correctly for this vehicle."
                                      persistent-hint/>
                            <template v-if="selectedDevice['battery_voltage_preset'] === 'custom'">
                                <v-text-field v-model.number="selectedDevice['battery_low_voltage']" type="number"
                                              step="0.1" density="compact" variant="outlined" class="mt-2"
                                              label="Low voltage (0%)"/>
                                <v-text-field v-model.number="selectedDevice['battery_warn_voltage']" type="number"
                                              step="0.1" density="compact" variant="outlined" class="mt-2"
                                              label="Warning voltage (optional)"/>
                                <v-text-field v-model.number="selectedDevice['battery_high_voltage']" type="number"
                                              step="0.1" density="compact" variant="outlined" class="mt-2"
                                              label="High voltage (100%)"/>
                            </template>
                        </v-col>
                        <!-- Map display preference. Phrased positively ("Show on
                             map") so it can never be read as switching tracking
                             off, and spelled out underneath for the same reason. -->
                        <v-col cols="12">
                            <v-switch v-model="selectedDevice['show_on_map']" color="primary"
                                      density="compact" hide-details
                                      label="Show on map"/>
                            <p class="text-caption text-medium-emphasis mt-1 mb-0">
                                Turn this off to keep the tracker out of the live map. It keeps tracking, and its
                                trips and alerts carry on as normal.
                            </p>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 pb-4 d-flex justify-end">
                    <v-btn variant="text" @click="editDialogSwitch = false">Cancel</v-btn>
                    <v-btn color="primary" type="submit">Save Changes</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import ThBtn from "@/components/globals/th-btn.vue";
import {useClipboard} from "@/composables/clipboard";
import {powerSourceIcon, batteryDisplay} from "@/composables/usePowerSourceIcon";
import {useSnackbarStore} from "@/stores/snackBarStore";
import AssignDevice from "@/components/dialogs/devices/RegisterWizard.vue";
import FiltersDialog from "@/components/cards/devices/FiltersDialog.vue";
import {formatDateTime} from "@/composables/useTimezone";
import {useNavigation} from "@/composables/globals";
import {isValidAddress} from "@/composables/useGeocode";
import {consumerPersonas, personaByAssetTypeId} from "@/composables/personaService";
import SubjectAvatar from "@/components/SubjectAvatar.vue";
import {useAuthStore} from "@/stores/authStore";

const authStore = useAuthStore();
// The store already derives both of these from the server's own profile flags,
// and this page was ignoring them for a locally recomputed owner test. That was
// wrong in both directions once the role tiers landed: a promoted co-parent
// (admin) could not register a tracker even though the server allows it, and a
// minor was offered Edit even though the server refuses it.
//
// Registering draws down the plan the OWNER pays for, so it is admin-or-owner.
// Renaming a tracker is not a plan action, so it is anyone who is not a minor.
const canRegister = computed(() => authStore.isCircleAdmin);
const canRename = computed(() => !authStore.isMinor);
// Table-driven persona list for the picker (falls back to the hardcoded set).
const personas = computed(() => consumerPersonas());
// Circle members, so a person-type device can be linked to one (sharing photo).
const circleMembers = ref([]);
const memberOptions = computed(() => circleMembers.value.map((m) => ({
    title: `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.email,
    value: m.id,
})));
const selectedIsPerson = computed(() =>
    personaByAssetTypeId(selectedDevice.value?.asset_type_id)?.kind === 'person');
// Hardware vehicle: the battery voltage range only applies here (not phones/people).
const selectedIsVehicle = computed(() =>
    personaByAssetTypeId(selectedDevice.value?.asset_type_id)?.kind === 'vehicle');
const fetchCircleMembers = () => {
    const cid = authStore.user?.company?.id;
    if (!cid) return;
    send('get', `companies/${cid}/users`)
        .then((response) => {
            circleMembers.value = response.data?.data?.users?.data ?? [];
        })
        .catch(() => {});
};
const {navigateTo} = useNavigation();
const assetTypes = ref();
const errors = ref([]);
const devices = ref([]);
const image = ref(null);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const imageFile = ref(null);
const totalDevices = ref(0);
const sortedType = ref(null);
const device_counts = ref([]);
const device_groups = ref([]);
const sortedColumn = ref(null);
const selectedDevice = ref(null);
const {copyToClipboard} = useClipboard();
const {showSnackbar} = useSnackbarStore();
const editDialogSwitch = ref(false);
const assignDialogSwitch = ref(false);
const filtersDialogSwitch = ref(false);
const filters = reactive({
    serial_num: {label: 'Serial Number', data: null, action: null},
    label: {label: 'Nick Name', data: null},
    imei: {label: 'IMEI', data: null, action: null},
    geo_radius: {label: 'Radius', type: 'number', data: null, latitude: null, longitude: null},
    iccid: {label: 'ICC ID', data: null, action: null},
    sku: {label: 'SKU', type: 'text', data: null},
    // 'voltage' is the SKU-selected channel on the server (an OBD unit's 12 V
    // rail, not its supercap on voltage1); phones are excluded server-side.
    voltage: {label: 'Battery Voltage (V)', type: 'number', data: null, action: null, description: 'Vehicle trackers only'},
});
const activeFilters = computed(() => {
    const activatedfilters = {};
    for (const [key, filter] of Object.entries(filters)) {
        if (filter.data !== null) {
            activatedfilters[key] = filter;
        }
    }
    return activatedfilters;
});
const imagePreview = () => {
    image.value = URL.createObjectURL(imageFile.value);
}
const updateDevices = (response) => {
    loading.value = true;
    devices.value = response.data.devices.data;
    totalDevices.value = response.data.devices.total;
    totalPages.value = response.data.devices.last_page;
    currentPage.value = response.data.devices.current_page;
    device_counts.value = response.data.device_counts;
    loading.value = false;
}

const removeFilter = (key) => {
    filters[key]['data'] = null;
    filters[key]['action'] ? filters[key]['action'] = null : '';
    totalPages.value = 1;
    currentPage.value = 1;
    totalDevices.value = 0;
    sortedType.value = null;
    fetchDevices();
}
const clearFilters = () => {
    Object.keys(filters).forEach((key) => {
        ['data', 'action', 'latitude', 'longitude'].forEach(field => {
            if (filters[key] && filters[key].hasOwnProperty(field)) {
                filters[key][field] = null;
            }
        });
    });
    totalPages.value = 1;
    currentPage.value = 1;
    totalDevices.value = 0;
    sortedType.value = null;
    fetchDevices();
}
const status = (status) => {
    switch (status) {
        case -1:
            return 'Non Reporting';
        case 0:
            return 'Active';
        case 1:
            return 'Disabled';
        case 2:
            return 'Faulty';
        case 3:
            return 'Lost';
    }
};
const statusChipColor = (status) => {
    switch (status) {
        case -1: return 'error';
        case 0: return 'success';
        case 1: return 'grey';
        case 2: return 'warning';
        case 3: return 'error';
        default: return 'grey';
    }
};
const signals = (strength) => {
    switch (strength) {
        case 0:
            return {
                icon: 'mdi-signal-off',
                color: '#dbdbdb'
            }
        case 1:
            return {
                icon: 'mdi-signal-cellular-outline',
                color: '#d70000'
            }
        case 2:
            return {
                icon: 'mdi-signal-cellular-1',
                color: '#ff9e01'
            }
        case 3:
            return {
                icon: 'mdi-signal-cellular-2',
                color: '#849e00'
            }
        case 4:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
        case 5:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
    }
}
const battery = (batteryBars, batteryType = null) => {
    return powerSourceIcon(batteryBars, batteryType);
}
const statusColor = (battery_bars, last_updated) => {
    let color = battery_bars ? battery(battery_bars)['color'] : 'red'
    color = getDifferenceInHours(last_updated) <= 30 ? color : 'red';
    switch (color) {
        case '#d70000':
        case 'red':
            return {
                color: color,
                message: "Device not communicated"
            }
        case '#ff9e01':
        case 'amber':
            return {
                color: color,
                message: "Device communicated rarely"
            }
        case '#849e00':
        case '#099f00':
        case 'green':
            return {
                color: color,
                message: "Device communicated recently"
            }
    }
}

const deviceStats = ((device) => {
    const battery_bars = (device.battery1_status ? device.battery1_bars : (device.battery2_status ? device.battery2_bars : 0))
    // batteryDisplay renders a phone's exact percent ("58%"); hardware keeps bars.
    const disp = batteryDisplay({...device, battery_bars});
    return {
        'battery_bars': battery_bars,
        'battery': {icon: disp.icon, color: disp.color},
        'battery_label': disp.label,
        'status_color': statusColor(battery_bars, device.last_updated),
    }
})

const subscriptionStatus = (device) => {
    if (!device.subscription_end_date) return null;
    const dateStr = device.subscription_end_date;
    const endDate = new Date(dateStr.includes('T') || dateStr.includes('+') || dateStr.endsWith('Z') ? dateStr : dateStr + 'T23:59:59Z');
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return 'expired';
    if (daysLeft <= 30) return 'expiring';
    return 'active';
};
const expiredDeviceCount = computed(() => devices.value.filter(d => subscriptionStatus(d) === 'expired').length);
const expiringDeviceCount = computed(() => devices.value.filter(d => subscriptionStatus(d) === 'expiring').length);

const assetType = (Id) => {
    if (assetTypes.value) {
        return assetTypes.value.find((item) => item.id === Id) ?? null;
    }
}

// Auto-refresh the device list when this circle's data changes elsewhere. The
// API publishes c/{companyId}/reload on every device/asset/config save; Live.vue
// subscribes and dispatches this window event. Debounced so a burst = one fetch.
let reloadTimer = null;
const onCompanyDataReload = () => {
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => fetchDevices(false), 800);
};
onMounted(() => {
    fetchDevices();
    fetchAssetTypes();
    // Anyone who is not a minor may link a person-type tracker to a member, so
    // the roster is needed beyond the owner.
    if (!authStore.isMinor) fetchCircleMembers();
    window.addEventListener('company-data-reload', onCompanyDataReload);
});
onBeforeUnmount(() => {
    clearTimeout(reloadTimer);
    window.removeEventListener('company-data-reload', onCompanyDataReload);
});

const getDifferenceInHours = (timestamp) => {
    const givenTime = new Date(timestamp);
    const currentTime = new Date();
    const differenceInMs = currentTime - givenTime;
    return (differenceInMs / (1000 * 60 * 60)).toFixed(0);
}
const copyAddress = (device) => {
    if (isValidAddress(device.formatted_address)) {
        copyToClipboard(device.formatted_address)
    } else {
        copyToClipboard(`${device.latitude}, ${device.longitude}`);
    }
}
const openStreetView = (device) => {
    if (isValidAddress(device.formatted_address)) {
        window.open(`http://maps.google.com/maps/dir//${device.formatted_address ?? ''}`, "_blank");
    } else {
        window.open(`http://maps.google.com/maps/dir//${device.latitude},${device.longitude}`, "_blank")
    }
};
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    currentPage.value = 1
    fetchDevices();
}
const computeDeviceStats = () => {
    if (devices.value.length > 0) {
        devices.value.forEach(device => {
            device.stats = deviceStats(device);
        })
    }
}

const fetchDevices = (showLoading = true) => {
    let data = {
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
    };
    // Background refreshes (e.g. an MQTT reload) skip the loading flag so the
    // table refreshes in place instead of flashing to a full-page spinner.
    if (showLoading) loading.value = true;
    if (Object.keys(activeFilters.value).length) {
        send('post', `personal-trackers/list-filters`, {...data, ...filters})
            .then((response) => {
                devices.value = response.data.data.devices.data;
                totalDevices.value = response.data.data.devices.total;
                totalPages.value = response.data.data.devices.last_page;
                currentPage.value = response.data.data.devices.current_page;
                device_counts.value = response.data.data.device_counts;
                device_groups.value = response.data.data.device_groups;
                computeDeviceStats();
            })
            .catch((error) => {
                console.log(error)
                showSnackbar('Trackers could not be loaded', 'error')
            }).finally(() => loading.value = false)
    } else {
        send('get', `personal-trackers/list`, data)
            .then((response) => {
                devices.value = response.data.data.devices.data;
                totalDevices.value = response.data.data.devices.total;
                totalPages.value = response.data.data.devices.last_page;
                currentPage.value = response.data.data.devices.current_page;
                device_counts.value = response.data.data.device_counts;
                device_groups.value = response.data.data.device_groups;
                computeDeviceStats();
            })
            .catch((error) => {
                console.log(error)
                showSnackbar('Trackers could not be loaded', 'error')
            }).finally(() => loading.value = false)
    }
}
const fetchAssetTypes = () => {
    send('get', `asset-types`, {list: true})
        .then((response) => {
            assetTypes.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Asset types could not be loaded', 'error')
        })
}
const addDevice = (device) => {
    devices.value.unshift(device);
}
const editDevice = (device) => {
    selectedDevice.value = {
        id: device.id,
        label: device.label,
        asset_type_id: device.asset_type_id,
        member_user_id: device.member_user_id ?? null,
        is_phone: device.is_phone ?? false,
        battery_voltage_preset: device.battery_voltage_preset ?? null,
        battery_low_voltage: device.battery_low_voltage ?? null,
        battery_warn_voltage: device.battery_warn_voltage ?? null,
        battery_high_voltage: device.battery_high_voltage ?? null,
        // Stored inverted server-side (hidden_on_map); the form asks the
        // positive question so nobody reads it as "stop tracking".
        show_on_map: !device.hidden_on_map,
    };
    editDialogSwitch.value = true;
}
// Update Device Info
const updateDevice = () => {
    errors.value = [];
    if (!selectedDevice.value.label) {
        errors.value.push('Nick Name is required field');
        return false;
    }
    if (!selectedDevice.value.asset_type_id) {
        errors.value.push('Asset Type is required field');
        return false;
    }
    // Battery voltage range (hardware vehicles only). A named preset sends just
    // the string (server fills canonical numbers); custom sends low/warn/high
    // with high > low. Vehicles default to 12V when none is set (matches backfill).
    const batteryFields = {};
    if (selectedIsVehicle.value && !selectedDevice.value['is_phone']) {
        const preset = selectedDevice.value['battery_voltage_preset'] || '12v';
        if (preset === 'custom') {
            const low = selectedDevice.value['battery_low_voltage'];
            const high = selectedDevice.value['battery_high_voltage'];
            // Reject empty/null too (Number('') is 0, which would slip past a
            // pure numeric check).
            if (low == null || low === '' || high == null || high === '' ||
                Number(high) <= Number(low)) {
                errors.value.push('Enter a valid custom battery range (high must be above low).');
                return false;
            }
            batteryFields.battery_voltage_preset = 'custom';
            batteryFields.battery_low_voltage = low;
            if (selectedDevice.value['battery_warn_voltage'] != null) {
                batteryFields.battery_warn_voltage = selectedDevice.value['battery_warn_voltage'];
            }
            batteryFields.battery_high_voltage = high;
        } else {
            batteryFields.battery_voltage_preset = preset;
        }
    }
    send('post', `personal-trackers/update/${selectedDevice.value.id}`, {
        _method: 'put',
        file: imageFile.value,
        label: selectedDevice.value['label'],
        asset_type: selectedDevice.value['asset_type_id'],
        member_user_id: selectedDevice.value['member_user_id'] ?? '',
        // 1/0 rather than true/false: this goes over multipart, where Laravel's
        // `boolean` rule accepts the digits but rejects the words.
        hidden_on_map: selectedDevice.value['show_on_map'] ? 0 : 1,
        ...batteryFields
    }, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then((response) => {
            showSnackbar(response.data.message);
            if (response.data.success) {
                devices.value.map((device, index) => {
                    if (device.id === selectedDevice.value.id) {
                        devices.value[index]['photo'] = image.value;
                        devices.value[index]['label'] = selectedDevice.value['label'];
                        devices.value[index]['asset_type_id'] = selectedDevice.value['asset_type_id'];
                        // Keeps the row badge in step without a full refetch.
                        devices.value[index]['hidden_on_map'] = !selectedDevice.value['show_on_map'];
                    }
                });
            }
            editDialogSwitch.value = false;
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            showSnackbar('Something went wrong', 'error');
        })
        .finally(() => {
            image.value = null;
            imageFile.value = null;
        })
}
</script>

<style scoped>
.persona-picker {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}

.persona-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 10px 4px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 12px;
    cursor: pointer;
    transition: border-color .15s ease, background-color .15s ease;
}

.persona-option:hover {
    border-color: rgba(0, 0, 0, 0.3);
}

.persona-option-label {
    font-size: 11px;
    font-weight: 600;
}
</style>
